import React, { useState } from "react";
import ModalOverlay from "./ui/ModalOverlay";
import ChecklistItem from "./ui/ChecklistItem";
import YouTubeLightbox, { extractYouTubeId } from "./YouTubeLightbox";

const WelcomeModalV2 = ({ householdData, onGetStarted }) => {
  const [lightboxUrl, setLightboxUrl] = useState(null);

  if (!householdData) return null;

  // Data extraction
  const advisorDetails = householdData.advisor_details || {};
  const advisorName = `${advisorDetails.first_name || ""} ${advisorDetails.last_name || ""}`.trim();
  const videoLink = advisorDetails.video_link || process.env.REACT_APP_ADVISOR_VIDEO_URL || "";
  const demoVideoUrl = process.env.REACT_APP_DEMO_VIDEO_URL || "";

  const primaryMember = (householdData.members || []).find(
    (m) => m.is_primary === true
  );
  const clientFirstName = primaryMember?.personal_info?.first_name || "";

  // YouTube thumbnail
  const videoId = extractYouTubeId(videoLink);
  const thumbnailUrl = videoId
    ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
    : null;

  return (
    <>
      <ModalOverlay>
        <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 sm:p-8">
            {/* Header */}
            <h1 className="text-xl sm:text-2xl font-bold text-emerald-600 mb-6 text-center">
              Secure data collection for your account transition
            </h1>

            {/* Divider */}
            <div className="border-b border-gray-200 mb-6"></div>

            {/* Two-column layout */}
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              {/* Left Column - Video Area */}
              <div className="md:w-5/12 flex-shrink-0">
                <button
                  onClick={() => videoLink && setLightboxUrl(videoLink)}
                  className="relative w-full rounded-xl overflow-hidden group cursor-pointer block"
                  disabled={!videoLink}
                >
                  {/* Thumbnail or Placeholder */}
                  {thumbnailUrl ? (
                    <img
                      src={thumbnailUrl}
                      alt="Advisor video message"
                      className="w-full aspect-video object-cover"
                      onError={(e) => {
                        e.target.style.display = "none";
                        if (e.target.nextSibling) {
                          e.target.nextSibling.style.display = "flex";
                        }
                      }}
                    />
                  ) : null}
                  {/* Placeholder (shown when no thumbnail or on error) */}
                  <div
                    className={`w-full aspect-video bg-gradient-to-br from-gray-700 to-gray-900 items-center justify-center ${
                      thumbnailUrl ? "hidden" : "flex"
                    }`}
                  >
                    <svg
                      className="w-12 h-12 text-white opacity-50"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </div>

                  {/* Play Button Overlay */}
                  {videoLink && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-14 h-14 bg-emerald-500 bg-opacity-90 rounded-full flex items-center justify-center shadow-lg group-hover:bg-opacity-100 group-hover:scale-110 transition-all duration-200">
                        <svg
                          className="w-6 h-6 text-white ml-1"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  )}

                  {/* Bottom Overlay - Advisor Info */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent px-4 py-3">
                    <p className="text-[10px] sm:text-xs font-semibold text-white/80 uppercase tracking-wider">
                      Message from your advisor
                    </p>
                    {advisorName && (
                      <p className="text-sm sm:text-base font-bold text-white">
                        {advisorName}
                      </p>
                    )}
                  </div>
                </button>

                {/* Watch intro text */}
                {videoLink && (
                  <p className="text-xs text-gray-500 mt-2 flex items-center justify-center gap-1">
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Watch a 1-minute intro
                  </p>
                )}
              </div>

              {/* Right Column - Content */}
              <div className="md:w-7/12">
                {/* Greeting */}
                {clientFirstName && (
                  <p className="text-base sm:text-lg font-bold text-gray-900 mb-3">
                    Hi {clientFirstName},
                  </p>
                )}

                {/* Description */}
                <p className="text-sm sm:text-base text-gray-700 mb-5 leading-relaxed">
                  We've pre-filled your information as much as possible. To
                  ensure a smooth transition, we just need you to review and
                  complete missing info, by uploading documents and adding data.
                </p>

                {/* What to Expect */}
                <div className="bg-gray-50 rounded-xl p-4 mb-5">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">
                    What to expect next:
                  </h3>
                  <ul className="space-y-2.5">
                    <ChecklistItem text="Securely upload requested documents" />
                    <ChecklistItem text="Review pending data items (approx. 5 mins)" />
                    <ChecklistItem text="Confirm & submit to your advisor" />
                  </ul>
                </div>

                {/* Sign-off */}
                <div className="text-sm sm:text-base text-gray-700">
                  <p>Regards,</p>
                  {advisorName && (
                    <p className="font-semibold text-gray-900">{advisorName}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Bottom Section */}
            <div className="mt-8 text-center">
              {/* Get Started Button */}
              <button
                onClick={onGetStarted}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-10 py-3.5 rounded-full font-semibold text-lg transition duration-200 shadow-lg hover:shadow-xl"
              >
                Get Started
              </button>

              {/* Security Note */}
              <p className="text-xs text-gray-400 mt-4">
                Your progress is saved automatically. Banking-level security
                enabled.
              </p>

              {/* Demo Video Note */}
              {demoVideoUrl && (
                <button
                  onClick={() => setLightboxUrl(demoVideoUrl)}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 transition"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Note: A full demo video is available at any time to guide you.
                </button>
              )}
            </div>
          </div>
        </div>
      </ModalOverlay>

      {/* YouTube Lightbox */}
      <YouTubeLightbox
        videoUrl={lightboxUrl}
        isOpen={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
      />
    </>
  );
};

export default WelcomeModalV2;
